import { GetServerSideProps } from 'next';
import React, { useState } from 'react';
import Head from 'next/head';
import PartnerPage from '@/modules/partner/partner-display-page';
import { relatedPartnersCarouselHandler } from '@/modules/partner/related-partners-carousel';
import { partnerInfoHandler } from '@/modules/partner/partner-info';
import { PartnerInfoProperties } from '@/modules/partner/partner-info/partner-info.types';
import { shopNowButtonHandler } from '@/modules/shop-now-button/shop-now-button.handler';
import { RelatedPartnersCarouselProperties } from '@/modules/partner/related-partners-carousel/related-partners-carousel.types';
import { stripHtmlDecode } from '@/utils/strip-html-decode/strip-html-decode';
import { ShopNowButtonHandlerProperties } from '@/modules/shop-now-button/shop-now-button.types';
import { PartnerPromotionalBannerProperties } from '@/modules/partner/partner-promotional-banner/partner-promotional-banner.types';
import { partnerPromotionalBannerHandler } from '@/modules/partner/partner-promotional-banner';
import ErrorBanner from '@/components/feedback/error-banner';
import { featureTogglesHandler } from '@/utils/shared-handler/feature-toggle-handler';
import { FEATURE_TOGGLE_LIST } from '@/context/feature-toggles/feature-toggles.types';

export type PartnerDisplayPageProperties = {
  pageTitle: string;
  partnerInfoProperties: PartnerInfoProperties;
  shopNowButtonProperties: ShopNowButtonHandlerProperties;
  relatedPartnersCarouselProperties: RelatedPartnersCarouselProperties;
  promotionalBannerProperties: PartnerPromotionalBannerProperties | null;
  hrefLang: string;
  href: string;
};

const PartnerDisplayPage = ({
  pageTitle,
  partnerInfoProperties,
  shopNowButtonProperties,
  relatedPartnersCarouselProperties,
  promotionalBannerProperties,
  hrefLang,
  href,
}: PartnerDisplayPageProperties) => {
  const {
    about: { text },
    errorLabels,
  } = partnerInfoProperties;
  const [showError, setShowError] = useState<boolean>(false);

  const stripDescription = stripHtmlDecode(text);

  return (
    <>
      <Head>
        <link rel="alternate" hrefLang={hrefLang} href={href} />
        <meta name="description" content={stripDescription} />
        <meta property="og:description" content={stripDescription} />
        <meta property="twitter:description" content={stripDescription} />
        <title>{pageTitle}</title>
      </Head>

      <>
        {showError && (
          <ErrorBanner
            setShowError={setShowError}
            closeLabel={errorLabels.close}
            message={errorLabels.message}
            title={errorLabels.title}
          />
        )}
        <PartnerPage
          shopNowButtonProperties={shopNowButtonProperties}
          partnerInfoProperties={partnerInfoProperties}
          relatedPartnersCarouselProperties={relatedPartnersCarouselProperties}
          promotionalBannerProperties={promotionalBannerProperties}
          setShowError={setShowError}
        />
      </>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<
  PartnerDisplayPageProperties
> = async context => {
  const { pageTitle, locale, baseUrl } = context.req.opco;

  const [
    featureToggles,
    partnerInfoProperties,
    shopNowButtonProperties,
    relatedPartnersCarouselProperties,
    promotionalBannerProperties,
  ] = await Promise.all([
    featureTogglesHandler(context.req.opco),
    partnerInfoHandler(context, context.params.slug),
    shopNowButtonHandler(context),
    relatedPartnersCarouselHandler(context, context.params.slug),
    partnerPromotionalBannerHandler(context, context.params.slug),
  ]);

  // some partners comes with eStore and CLO collection method
  // if the CLO feature is disabled, we shouldn't render any reference
  // related to CLO collection method that was assigned to that partner
  // example Selfridges Vueling
  if (
    partnerInfoProperties &&
    !featureToggles.includes(FEATURE_TOGGLE_LIST.CLO)
  ) {
    const propertiesToDelete: Array<keyof PartnerInfoProperties> = [
      'card',
      'purchaseConditions',
      'rates',
      'terms',
      'tabs',
    ];
    for (const property of propertiesToDelete) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((partnerInfoProperties as any)[property]?.CLO !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (partnerInfoProperties as any)[property].CLO;
      }
    }
  }

  if (!partnerInfoProperties || !relatedPartnersCarouselProperties) {
    return {
      notFound: true,
    };
  }

  const { pageTitle: partnerPageTitle, destinationUrl } = partnerInfoProperties;

  return {
    props: {
      pageTitle: partnerPageTitle
        ? `${partnerPageTitle} | ${pageTitle}`
        : pageTitle,
      partnerInfoProperties,
      shopNowButtonProperties,
      relatedPartnersCarouselProperties,
      promotionalBannerProperties,
      hrefLang: locale.toLowerCase(),
      href: `${baseUrl}/${locale}${destinationUrl}`,
    },
  };
};

export default PartnerDisplayPage;
